import React, { useState } from 'react';
import { Loader2Icon, StarIcon } from 'lucide-react';
import type { StarRating } from '../../types/review';
import { useLanguage } from '../../contexts/LanguageContext';
import { reviewsConfig } from '../../data/reviews';
import { FormField, inputClasses } from '../ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { sendFormEmail } from '../../utils/formMail';
type ReviewFormProps = {
  onSubmit: (r: {
    rating: StarRating;
    title: string;
    body: string;
    author: string;
    location?: string;
    purchase?: string;
  }) => void;
  onCancel: () => void;
};
const STAR_LABELS_EN = ['Poor', 'Fair', 'Good', 'Very good', 'Excellent'];
const STAR_LABELS_ES = ['Malo', 'Regular', 'Bueno', 'Muy bueno', 'Excelente'];
export function ReviewForm({
  onSubmit,
  onCancel
}: ReviewFormProps) {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const [rating, setRating] = useState<StarRating | 0>(0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({
    title: '',
    body: '',
    author: '',
    email: '',
    location: '',
    purchase: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err: Record<string, string> = {};
    if (!rating) err.rating = es ? 'Elige una calificación.' : 'Choose a star rating.';
    if (form.title.trim().length < 3) err.title = es ? 'Agrega un título.' : 'Add a title.';
    if (form.body.trim().length < 20) err.body = es ? 'Escribe al menos 20 caracteres.' : 'Write at least 20 characters.';
    if (!form.author.trim()) err.author = es ? 'Agrega tu nombre.' : 'Add your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) err.email = es ? 'Correo no válido.' : 'Enter a valid email.';
    setErrors(err);
    if (Object.keys(err).length || !rating) return;
    setSending(true);
    setSendError('');
    // Every review — any star rating — goes to the store inbox for approval.
    sendFormEmail(`Review for approval — ${rating}★ — ${form.title.trim()}`, {
      Rating: `${rating} / 5`,
      Title: form.title.trim(),
      Review: form.body.trim(),
      Name: form.author.trim(),
      Email: form.email.trim(),
      Location: form.location.trim(),
      Purchase: form.purchase,
      'To publish': 'Approve it in your review app or reply to us to add it to data/reviews.ts'
    }, {
      replyTo: form.email.trim()
    }).then(() => {
      track('review_submitted', {
        rating
      });
      onSubmit({
        rating,
        title: form.title.trim(),
        body: form.body.trim(),
        author: form.author.trim(),
        location: form.location.trim() || undefined,
        purchase: form.purchase || undefined
      });
    }).catch(() => setSendError(es ? 'No pudimos enviar tu reseña. Inténtalo de nuevo.' : "We couldn't send your review. Please try again.")).finally(() => setSending(false));
  };
  const labels = es ? STAR_LABELS_ES : STAR_LABELS_EN;
  const shown = hover || rating;
  return <form noValidate onSubmit={submit} className="rounded-2xl border mf-hairline bg-navy p-5 md:p-7" data-id="element-676" data-mp="1yqsxsz1gygzt">
      <fieldset data-id="element-677" data-mp="23obb11miridm">
        <legend className="text-sm font-medium text-white" data-id="element-678" data-mp="23pw5kr8u31bh">{es ? 'Tu calificación' : 'Your rating'}</legend>
        <div className="mt-2 flex items-center gap-3" data-id="element-679" data-mp="28mmk3a4zpous">
          <div role="radiogroup" aria-label={es ? 'Calificación' : 'Rating'} className="flex" onMouseLeave={() => setHover(0)} data-id="element-680" data-mp="2dffmvsgwen48">
            {[1, 2, 3, 4, 5].map(n => <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} — ${labels[n - 1]}`} onClick={() => setRating(n as StarRating)} onMouseEnter={() => setHover(n)} className="flex h-11 w-11 items-center justify-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-681" data-mp="1fxparwirb2e6">
                <StarIcon className={`h-8 w-8 transition-colors duration-100 ${n <= shown ? 'fill-[#FFC94D] text-[#FFC94D]' : 'text-titanium/40'}`} strokeWidth={1.5} aria-hidden data-id="element-682" data-mp="3rlkc5ijpw0eu" />
              </button>)}
          </div>
          {shown > 0 && <span className="text-sm text-titanium" data-id="element-683" data-mp="j4plr2k3w8hn">{labels[shown - 1]}</span>}
        </div>
        {errors.rating && <p className="mt-1.5 text-sm text-red-300" data-id="element-684" data-mp="2kyf720c6oion">{errors.rating}</p>}
      </fieldset>

      <div className="mt-6 grid gap-5 md:grid-cols-2" data-id="element-685" data-mp="2l09rldjjm8s7">
        <div className="md:col-span-2" data-id="element-686" data-mp="2g0ner57mmujx">
          <FormField id="rv-title" label={es ? 'Título' : 'Review title'} error={errors.title} data-id="element-687" data-mp="2g1opt9on7h1n">
            <input className={inputClasses} value={form.title} onChange={set('title')} maxLength={80} data-id="element-688" data-mp="vjnnae2aylsz" />
          </FormField>
        </div>
        <div className="md:col-span-2" data-id="element-689" data-mp="ls1d8l1pasrt">
          <FormField id="rv-body" label={es ? 'Tu reseña' : 'Your review'} error={errors.body} data-id="element-690" data-mp="lr84xfn3fv7k">
            <textarea rows={5} className={inputClasses} value={form.body} onChange={set('body')} maxLength={2000} data-id="element-691" data-mp="qp050wxw6nmu" />
          </FormField>
        </div>
        <FormField id="rv-name" label={es ? 'Nombre (se mostrará)' : 'Name (displayed)'} error={errors.author} data-id="element-692" data-mp="hqfvalab7oar">
          <input className={inputClasses} value={form.author} onChange={set('author')} autoComplete="given-name" data-id="element-693" data-mp="2tffng5e7jsn" />
        </FormField>
        <FormField id="rv-email" label={es ? 'Correo (no se muestra)' : 'Email (not displayed)'} error={errors.email} hint={es ? 'Para verificar tu compra.' : 'Used to verify your purchase.'} data-id="element-694" data-mp="7s8pmylh0so2">
          <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-695" data-mp="20ob3v6u1mzox" />
        </FormField>
        <FormField id="rv-location" label={es ? 'Ciudad, estado (opcional)' : 'City, state (optional)'} data-id="element-696" data-mp="1voq4wwevirc7">
          <input className={inputClasses} value={form.location} onChange={set('location')} data-id="element-697" data-mp="1qqwyc29nrvbe" />
        </FormField>
        <FormField id="rv-purchase" label={es ? '¿Qué compraste? (opcional)' : 'What did you buy? (optional)'} data-id="element-698" data-mp="1mqmfvq4ojcfn">
          <select className={inputClasses} value={form.purchase} onChange={set('purchase')} data-id="element-699" data-mp="2jh6oxci68te5">
            <option value="" data-id="element-700" data-mp="2ei2def7ryjkn">—</option>
            {reviewsConfig.purchaseOptions.map(o => <option key={o} value={o} data-id="element-701" data-mp="29n68iehfw1q4">
                {o}
              </option>)}
          </select>
        </FormField>
      </div>

      {sendError && <p role="alert" className="mt-6 text-sm text-red-300" data-id="element-702" data-mp="326lkwsj02sbo">{sendError}</p>}
      <div className="mt-7 flex flex-col gap-3 sm:flex-row" data-id="element-703" data-mp="2xebnysjgwrzw">
        <button type="submit" disabled={sending} className={buttonClasses('primary')} data-id="element-704" data-mp="2xcqsn0pbx50z">
          {sending && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden data-id="element-705" data-mp="jyaacc01t3ok" />}
          {es ? 'Enviar reseña' : 'Submit review'}
        </button>
        <button type="button" onClick={onCancel} className={buttonClasses('ghost')} data-id="element-706" data-mp="swdvv8b1a0gd">
          {es ? 'Cancelar' : 'Cancel'}
        </button>
      </div>
      <p className="mt-4 text-xs text-titanium/70" data-id="element-707" data-mp="xx9y7hfqlejo">
        {es ? 'Nuestro equipo revisa cada reseña antes de publicarla.' : 'Every review is checked by our team before it’s published.'}
      </p>
    </form>;
}